'use client'

import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

import { VideoControl } from '@src/ds/blocks/video-control'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackInternalEvent } from '@src/telemetry/track'

import type { VideoDSProps } from './video-ds.types'

export enum VIDEO_LOADING_TYPE {
  Lazy = 'lazy',
  Eager = 'eager',
}

export enum VIDEO_PRELOAD_TYPE {
  Auto = 'auto',
  Metadata = 'metadata',
  None = 'none',
}

// Matches the Contentful video host whether the URL is protocol-relative (`//videos.ctfassets.net/…`)
// or absolute (`https://videos.ctfassets.net/…`), so both forms get rewritten onto the Quince CDN.
// eslint-disable-next-line i18next/no-literal-string -- CDN host constant, not user-facing copy
const CONTENTFUL_VIDEO_REGEX = new RegExp('(?:https?:)?//videos\\.ctfassets\\.net/afruo3gr1g6h/', 'g')
const VIDEO_DOMAIN_NAME = 'videos.quince.com'

/** Rewrite Contentful video URLs onto the Quince CDN; report + pass through the original on failure. */
function transformVideoSrc(url: string): string {
  try {
    return url.replace(CONTENTFUL_VIDEO_REGEX, `//${VIDEO_DOMAIN_NAME}/`)
  } catch (error) {
    // eslint-disable-next-line i18next/no-literal-string -- internal error name
    const e = new Error('Failed to transform Video at transformVideoSrc', { cause: error })
    // eslint-disable-next-line i18next/no-literal-string -- internal error name
    e.name = 'Failed to transform Video'
    captureException(e, {
      module: MODULES.UNKNOWN,
      feature: FEATURES.VIDEO_TRANSFORMATION,
      extraInfo: { originalError: JSON.stringify(error), url },
    })
    return url
  }
}

/**
 * Resolve a video source to a fully-qualified URL: transform Contentful hosts onto the Quince CDN, then
 * add an explicit `https:` scheme only for protocol-relative URLs (`//host/…`). Absolute URLs
 * (`https://…`, `http://…`) are left untouched so they aren't corrupted into `https:https://…`.
 */
function resolveVideoSrc(url: string): string {
  const transformed = transformVideoSrc(url)
  // @i18n-ignore -- resolved media URL, not user-facing copy
  return transformed.startsWith('//') ? `https:${transformed}` : transformed
}

/**
 * Legacy `Video` atom's completion threshold. It sat at 85% there because time updates were throttled to
 * 1s; the ratio is kept for event parity even though this component reads `timeupdate` unthrottled (the
 * once-per-mount ref guard means the extra checks can't emit duplicates).
 */
const VIDEO_COMPLETION_THRESHOLD = 0.85

const wrapperClass = css({ width: 'full', overflow: 'hidden' })
const videoClass = css({ display: 'block', width: 'full', height: 'full', objectFit: 'cover' })

// Only applied when `limitedControls` is on, so no existing consumer gains a containing block.
const relativeWrapperClass = css({ position: 'relative' })

// Figma spacing frames: desktop 6998:22815 is 104×104 (`space-32` + the 40×40 control + `space-32`),
// mobile 6998:22837 is 72×72 (`space-16` + the 32×32 control + `space-16`). The frame's padding IS the
// control's inset from the media edge, so anchoring it bottom-right reproduces both frames exactly.
const controlInsetClass = css({
  position: 'absolute',
  bottom: 0,
  right: 0,
  display: 'flex',
  padding: 'space-16',
  zIndex: 1,
  lg: { padding: 'space-32' },
})

export function VideoDS({
  url,
  contentType,
  title,
  posterUrl,
  autoplay = false,
  loop = false,
  muted = false,
  playsInline = true,
  preload = VIDEO_PRELOAD_TYPE.None,
  loadingType = VIDEO_LOADING_TYPE.Lazy,
  limitedControls = false,
  trackCompletion = false,
  tracker,
  trackingProperties,
  className,
  wrapperClassName,
}: VideoDSProps) {
  const { t } = useTranslation('common')
  const src = resolveVideoSrc(url)
  const [videoRefFn, isIntersecting, intersectedElement] = useIntersect(null, '400px', 0, true)
  const videoEl = intersectedElement as HTMLVideoElement | null
  const [isAutoPlayStarted, setIsAutoPlayStarted] = useState(false)
  // Own handle on the element, so the play/pause control works regardless of intersection state.
  const videoNodeRef = useRef<HTMLVideoElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  // Mirror the real `.paused` state rather than tracking our own clicks, so the control also reflects
  // pauses we don't initiate (autoplay policy, the out-of-view rewind below, tab visibility, stream end).
  //
  // `videoEl` is in the dep list even though the listeners bind to `videoNodeRef`: it is the observed node
  // as React STATE, so it flips null -> element and re-runs this effect once the node actually exists. Keyed
  // only on `limitedControls`, the effect fired once against a possibly-empty ref and never re-bound — the
  // control then froze on "play" while the video played. `?.addEventListener` keeps the unit tests, which
  // feed `useIntersect` a plain object stub, from tripping over the DOM API.
  useEffect(() => {
    if (!limitedControls) return
    const video = videoNodeRef.current
    if (!video?.addEventListener) return
    const sync = () => {
      setIsPlaying(!video.paused)
    }
    sync()
    video.addEventListener('play', sync)
    video.addEventListener('pause', sync)
    return () => {
      video.removeEventListener('play', sync)
      video.removeEventListener('pause', sync)
    }
  }, [limitedControls, videoEl])

  // Port of the legacy `Video` atom's `VIDEO_COMPLETED` event, which fired for every video regardless of
  // whether a `tracker` was supplied. Opt-in so migrating a call site preserves its events without
  // retroactively instrumenting the `VideoDS` consumers that never had them.
  const hasFiredCompletedRef = useRef(false)
  const handleTimeUpdate = (video: HTMLVideoElement) => {
    if (!trackCompletion || hasFiredCompletedRef.current) return
    const { duration, currentTime } = video
    if (!duration || currentTime / duration < VIDEO_COMPLETION_THRESHOLD) return
    hasFiredCompletedRef.current = true
    trackInternalEvent(TELEMETRY_EVENT_NAMES.VIDEO_COMPLETED, { tracker, context: trackingProperties })
  }

  const handleTogglePlay = () => {
    const video = videoNodeRef.current
    if (!video) return
    if (video.paused) {
      // `Promise.resolve` keeps this safe where `play()` returns undefined instead of a promise.
      void Promise.resolve(video.play()).catch(() => {
        setIsPlaying(false)
      })
    } else {
      video.pause()
    }
  }

  useEffect(() => {
    if (!videoEl || !isIntersecting) {
      // Autoplay videos rewind + pause when scrolled out of view; non-autoplay videos keep their state.
      if (videoEl && autoplay) {
        videoEl.currentTime = 0
        if (videoEl.played.length) videoEl.pause()
        setIsAutoPlayStarted(false)
      }
      return
    }
    // Promote the lazy `data-src` to `src` on first intersection for every video (autoplay or not),
    // so a Lazy + non-autoplay primitive still loads its source instead of staying empty forever.
    const sourceElement = videoEl.querySelector('source')
    if (sourceElement && !sourceElement.src) {
      sourceElement.src = src
      videoEl.load()
    }
    // Only autoplay videos start playback programmatically; a non-autoplay video waits for the user.
    if (!autoplay || isAutoPlayStarted) return
    // `play()` predates its promise return value and still yields `undefined` in older mobile browsers and
    // Android / RN WebViews — the legacy `Video` atom guarded that with an explicit `playPromise !== undefined`
    // check. Calling `.then` on `undefined` throws inside this effect, which unmounts the whole component, so
    // the video AND its play/pause control disappear together. `Promise.resolve` normalises both shapes.
    void Promise.resolve(videoEl.play())
      .then(() => {
        setIsAutoPlayStarted(true)
      })
      .catch(() => {
        // Autoplay can be blocked by the browser; a muted looping media video degrades to a poster/first frame.
      })
  }, [autoplay, isAutoPlayStarted, isIntersecting, src, videoEl])

  return (
    <div className={cx(wrapperClass, limitedControls && relativeWrapperClass, wrapperClassName)}>
      <video
        ref={el => {
          videoNodeRef.current = el
          videoRefFn(el)
        }}
        title={title}
        autoPlay={autoplay}
        loop={loop}
        muted={muted}
        preload={preload}
        // Autoplay videos are decorative (no chrome); a non-autoplay video needs native controls to be
        // playable — unless `limitedControls` replaces them with the DS play/pause control below.
        controls={!autoplay && !limitedControls}
        disablePictureInPicture
        playsInline={playsInline}
        poster={posterUrl}
        className={cx(videoClass, className)}
        onTimeUpdate={
          trackCompletion
            ? e => {
                handleTimeUpdate(e.currentTarget)
              }
            : undefined
        }
      >
        <source type={contentType} src={loadingType === VIDEO_LOADING_TYPE.Eager ? src : undefined} data-src={src} />
      </video>
      {limitedControls && (
        <div className={controlInsetClass}>
          <VideoControl
            controlType="playback"
            active={isPlaying}
            size="sm-md"
            onClick={handleTogglePlay}
            aria-label={isPlaying ? t('pause_video') : t('play_video')}
          />
        </div>
      )}
    </div>
  )
}

export default VideoDS
